import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, resolveUrl } from "../../lib/api";
import { ArrowLeft, AlertTriangle, Gauge, Fuel, CheckCircle2, Loader2 } from "lucide-react";

export default function InspectionDiff() {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/inspections/diff/${reservationId}`)
      .then((r) => setData(r.data))
      .catch((e) => window.alert(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [reservationId]);

  if (loading) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!data) return null;

  const d = data.diff;
  const hasIssues = d && (d.new_exterior_damages?.length || d.new_interior_damages?.length || d.checklist_issues?.length);

  return (
    <div className="space-y-6" data-testid="inspection-diff">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="btn-ghost p-2">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="font-serif text-2xl">Raport diferente predare ↔ returnare</h1>
          <div className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>Rezervare #{reservationId?.slice(0, 8)}</div>
        </div>
      </div>

      {!data.has_pickup || !data.has_return ? (
        <div className="card-surface p-6 flex items-center gap-3" data-testid="diff-incomplete">
          <AlertTriangle className="w-5 h-5" style={{ color: "#f59e0b" }} />
          <div className="text-sm">
            {data.message || "Ambele inspectii (predare si returnare) trebuie sa existe pentru a calcula diferentele."}
            <div className="text-xs mt-1" style={{ color: "var(--brand-text-secondary)" }}>
              Predare: {data.has_pickup ? "✓ exista" : "✗ lipseste"} · Returnare: {data.has_return ? "✓ exista" : "✗ lipseste"}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="card-surface p-5">
              <div className="label-eyebrow text-[10px] mb-1 inline-flex items-center gap-1">
                <Gauge className="w-3 h-3" /> Kilometri parcursi
              </div>
              <div className="font-serif text-3xl" style={{ color: "var(--brand-primary)" }}>
                {(d.mileage_delta || 0).toLocaleString("ro-RO")} km
              </div>
            </div>
            <div className="card-surface p-5">
              <div className="label-eyebrow text-[10px] mb-1 inline-flex items-center gap-1">
                <Fuel className="w-3 h-3" /> Schimbare combustibil
              </div>
              <div className="font-serif text-3xl" style={{ color: d.fuel_delta < 0 ? "#f59e0b" : "var(--brand-primary)" }}>
                {d.fuel_delta > 0 ? "+" : ""}{d.fuel_delta}%
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <DamageSection title="Daune noi exterioare" items={d.new_exterior_damages} testid="new-exterior" />
            <DamageSection title="Daune noi interioare" items={d.new_interior_damages} testid="new-interior" />
          </div>

          <div data-testid="checklist-issues">
            <h3 className="font-serif text-lg mb-3">Probleme la lista de verificare</h3>
            {!d.checklist_issues || d.checklist_issues.length === 0 ? (
              <div className="card-surface p-4 text-sm inline-flex items-center gap-2" style={{ color: "#22c55e" }}>
                <CheckCircle2 className="w-4 h-4" /> Niciuna — toate punctele OK la returnare.
              </div>
            ) : (
              <div className="space-y-2">
                {d.checklist_issues.map((c, i) => (
                  <div key={i} className="card-surface p-3 flex items-center gap-3 text-sm" data-testid={`checklist-issue-${i}`}>
                    <AlertTriangle className="w-4 h-4 shrink-0" style={{ color: c.status === "missing" ? "#ef4444" : "#f59e0b" }} />
                    <div className="flex-1">
                      <span className="font-medium">{c.label}</span>
                      {c.note && <span className="ml-2 text-xs" style={{ color: "var(--brand-text-secondary)" }}>· {c.note}</span>}
                    </div>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: c.status === "missing" ? "#ef4444" : "#f59e0b", background: c.status === "missing" ? "rgba(239,68,68,0.15)" : "rgba(245,158,11,0.15)" }}>
                      {c.status === "missing" ? "Lipsa" : "Probleme"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {hasIssues ? (
            <div className="card-surface p-4 border-l-4 border-l-red-500" style={{ borderLeftColor: "#ef4444" }}>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: "#ef4444" }} />
                <div className="text-sm">
                  <div className="font-medium">Diferente detectate.</div>
                  <div style={{ color: "var(--brand-text-secondary)" }} className="text-xs mt-1">
                    Considera generarea unei facturi pentru daune sau a unei taxe aditionale pe baza acestor diferente.
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card-surface p-4" style={{ borderLeftColor: "#22c55e" }}>
              <div className="flex items-center gap-2 text-sm" style={{ color: "#22c55e" }}>
                <CheckCircle2 className="w-4 h-4" />
                Masina returnata in starea predarii. Nicio diferenta semnificativa.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DamageSection({ title, items, testid }) {
  return (
    <div data-testid={testid}>
      <h3 className="font-serif text-base mb-2">{title}</h3>
      {!items || items.length === 0 ? (
        <div className="card-surface p-4 text-sm" style={{ color: "#22c55e" }}>
          <CheckCircle2 className="w-4 h-4 inline mr-1" /> Nicio dauna noua.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((d, i) => (
            <div key={i} className="card-surface p-3 text-sm" data-testid={`${testid}-item-${i}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium">{d.location}</span>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: d.severity === "severe" ? "#ef4444" : d.severity === "moderate" ? "#f59e0b" : "#3b82f6", background: d.severity === "severe" ? "rgba(239,68,68,0.15)" : d.severity === "moderate" ? "rgba(245,158,11,0.15)" : "rgba(59,130,246,0.15)" }}>
                  {d.severity}
                </span>
              </div>
              <p className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>{d.description}</p>
              {d.photo_urls && d.photo_urls.length > 0 && (
                <div className="flex gap-1 mt-2 flex-wrap">
                  {d.photo_urls.map((u, j) => (
                    <a key={j} href={resolveUrl(u)} target="_blank" rel="noopener noreferrer">
                      <img src={resolveUrl(u)} alt="" className="w-16 h-16 object-cover rounded border" style={{ borderColor: "var(--brand-border)" }} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
